<template>
  <div class="bsm">
    <!-- Header (page title comes from the breadcrumb, so no repeated heading here) -->
    <div class="bsm-head">
      <div>
        <p class="bsm-sub">
          Sell grown birds to your lifters by weight grade.
        </p>
      </div>
      <div class="bsm-head-actions">
        <b-button
          variant="outline-primary"
          size="sm"
          @click="openLifters"
        >
          <feather-icon
            icon="UsersIcon"
            size="14"
            class="mr-25"
          /> Manage Lifters
        </b-button>
        <b-button
          variant="outline-secondary"
          size="sm"
          @click="openSettings"
        >
          <feather-icon
            icon="SettingsIcon"
            size="14"
            class="mr-25"
          /> Weight Settings
        </b-button>
      </div>
    </div>

    <!-- Mode toggle -->
    <div class="bsm-modes">
      <div
        class="bsm-mode"
        :class="{ active: mode === 'auto' }"
        @click="setMode('auto')"
      >
        <feather-icon
          icon="ZapIcon"
          size="16"
        />
        <div>
          <div class="bsm-mode-t">
            Automatic
          </div>
          <div class="bsm-mode-d">
            Flocks the weight engine flagged ready (1.7kg / 2.2kg)
          </div>
        </div>
      </div>
      <div
        class="bsm-mode"
        :class="{ active: mode === 'manual' }"
        @click="setMode('manual')"
      >
        <feather-icon
          icon="EditIcon"
          size="16"
        />
        <div>
          <div class="bsm-mode-t">
            Manual
          </div>
          <div class="bsm-mode-d">
            Pick any flock aged 30+ days and sell
          </div>
        </div>
      </div>
    </div>

    <!-- Ready flocks -->
    <div
      v-if="loadingFlocks"
      class="bsm-load"
    >
      <b-spinner
        small
        variant="primary"
      />
    </div>
    <template v-else>
      <div
        v-if="!flocks.length"
        class="bsm-empty"
      >
        <feather-icon
          icon="FeatherIcon"
          size="30"
          class="text-muted"
        />
        <h5 class="mt-1">
          {{ mode === 'auto' ? 'No flocks are ready yet' : 'No flocks aged 30+ days' }}
        </h5>
        <p class="text-muted">
          {{ mode === 'auto'
            ? 'When a flock crosses the configured weight, it appears here for allocation.'
            : 'Manual selling needs a flock at least 30 days old.' }}
        </p>
      </div>

      <!-- View toggle: cards vs fast table -->
      <div
        v-if="flocks.length"
        class="bsm-viewtoggle"
      >
        <span class="bsm-viewtoggle-lbl">{{ flocks.length }} flock{{ flocks.length > 1 ? 's' : '' }}</span>
        <div class="bsm-seg">
          <button
            type="button"
            class="bsm-seg-btn"
            :class="{ active: viewMode === 'cards' }"
            @click="viewMode = 'cards'"
          >
            <feather-icon icon="GridIcon" size="14" /> Cards
          </button>
          <button
            type="button"
            class="bsm-seg-btn"
            :class="{ active: viewMode === 'table' }"
            @click="viewMode = 'table'"
          >
            <feather-icon icon="ListIcon" size="14" /> Table
          </button>
        </div>
      </div>

      <!-- CARD VIEW (grouped farm-wise) -->
      <template v-if="viewMode === 'cards'">
        <div
          v-for="farm in flocksByFarm"
          :key="farm.farm_id"
          class="bsm-farm-group"
        >
          <div class="bsm-farm-head">
            <feather-icon icon="HomeIcon" size="15" class="mr-50" />
            <span class="bsm-farm-name">{{ farm.farm_name }}</span>
            <span class="bsm-farm-count">{{ farm.flocks.length }} {{ mode === 'auto' ? 'ready' : (farm.flocks.length > 1 ? 'flocks' : 'flock') }} · {{ Number(farm.totalBirds).toLocaleString('en-IN') }} birds</span>
          </div>

          <div class="bsm-grid">
            <div
              v-for="f in farm.flocks"
              :key="f.flock_id"
              class="bsm-card"
            >
              <div class="bsm-card-top">
                <div>
                  <div class="bsm-flock">
                    {{ f.flock_name || f.flock_id }}
                  </div>
                  <div class="bsm-flock-sub">
                    <span v-if="f.shed_name">{{ f.shed_name }} · </span>{{ Number(f.alive_birds).toLocaleString('en-IN') }} birds
                    <span v-if="f.age_days"> · {{ f.age_days }} days</span>
                  </div>
                </div>
                <b-badge
                  v-if="f.grade"
                  :variant="f.grade === 'b' ? 'light-primary' : 'light-success'"
                  pill
                >
                  {{ f.grade === 'b' ? '2.2 kg grade' : '1.7 kg grade' }}
                </b-badge>
              </div>
              <div class="bsm-card-stats">
                <div><span class="v">{{ f.avg_bird_weight ? f.avg_bird_weight + ' kg' : '—' }}</span><span class="l">avg weight</span></div>
                <div><span class="v">{{ f.est_qty_kg ? Number(f.est_qty_kg).toLocaleString('en-IN') : '—' }}</span><span class="l">est. kg</span></div>
              </div>
              <b-button
                block
                variant="primary"
                size="sm"
                @click="openAllocate(f)"
              >
                <feather-icon
                  icon="ShareIcon"
                  size="14"
                  class="mr-25"
                /> Allocate to lifters
              </b-button>
            </div>
          </div>
        </div>
      </template>

      <!-- TABLE VIEW (all flocks in one scan) -->
      <b-table
        v-else-if="flocks.length"
        :items="tableFlocks"
        :fields="flockTableFields"
        responsive
        small
        hover
        class="bsm-table"
      >
        <template #cell(batch)="{ item }">
          <span class="font-weight-bold">{{ item.flock_name || item.flock_id }}</span>
        </template>
        <template #cell(alive_birds)="{ item }">
          {{ Number(item.alive_birds).toLocaleString('en-IN') }}
        </template>
        <template #cell(age_days)="{ item }">
          {{ item.age_days != null ? item.age_days + ' d' : '—' }}
        </template>
        <template #cell(avg_bird_weight)="{ item }">
          {{ item.avg_bird_weight ? item.avg_bird_weight + ' kg' : '—' }}
        </template>
        <template #cell(est_qty_kg)="{ item }">
          {{ item.est_qty_kg ? Number(item.est_qty_kg).toLocaleString('en-IN') + ' kg' : '—' }}
        </template>
        <template #cell(grade)="{ item }">
          <b-badge
            v-if="item.grade"
            :variant="item.grade === 'b' ? 'light-primary' : 'light-success'"
            pill
          >
            {{ item.grade === 'b' ? '2.2 kg' : '1.7 kg' }}
          </b-badge>
          <span v-else class="text-muted">—</span>
        </template>
        <template #cell(actions)="{ item }">
          <b-button
            variant="primary"
            size="sm"
            @click="openAllocate(item)"
          >
            <feather-icon icon="ShareIcon" size="13" class="mr-25" /> Allocate
          </b-button>
        </template>
      </b-table>
    </template>

    <!-- Lifter requests -->
    <div class="bsm-req-head">
      <h5 class="mb-0">
        Lifter requests
      </h5>
      <b-button
        variant="flat-secondary"
        size="sm"
        @click="fetchRequests"
      >
        <feather-icon
          icon="RefreshCwIcon"
          size="14"
        />
      </b-button>
    </div>
    <div
      v-if="!requests.length"
      class="bsm-req-empty text-muted"
    >
      No pending requests from lifters.
    </div>
    <div
      v-else
      class="bsm-req-list"
    >
      <div
        v-for="r in requests"
        :key="r.id"
        class="bsm-req"
      >
        <div>
          <div class="bsm-req-lifter">{{ r.lifter ? r.lifter.name : 'Lifter' }}
            <span class="text-muted">· {{ r.lifter ? r.lifter.phone : '' }}</span></div>
          <div class="bsm-req-detail">
            {{ Number(r.qty_birds).toLocaleString('en-IN') }} {{ r.bird_type }} birds · ₹{{ r.price_per_kg }}/kg · {{ r.price_type }}
          </div>
        </div>
        <div class="bsm-req-actions">
          <b-badge
            v-if="r.status !== 'pending'"
            :variant="reqPill(r.status)"
            pill
          >
            {{ r.status }}
          </b-badge>
          <template v-else>
            <b-button
              size="sm"
              variant="flat-success"
              @click="approveRequest(r)"
            >
              Approve
            </b-button>
            <b-button
              size="sm"
              variant="flat-danger"
              @click="rejectRequest(r)"
            >
              Reject
            </b-button>
          </template>
        </div>
      </div>
    </div>

    <!-- ===== Allocate modal ===== -->
    <b-modal
      v-model="allocModal"
      :title="`Allocate — ${allocFlock ? (allocFlock.flock_name || allocFlock.flock_id) : ''}`"
      size="lg"
      hide-footer
    >
      <div v-if="allocFlock">
        <div class="alloc-summary">
          <span><strong>{{ Number(allocFlock.alive_birds).toLocaleString('en-IN') }}</strong> birds available</span>
          <span :class="remaining < 0 ? 'text-danger' : 'text-success'">
            {{ Number(remaining).toLocaleString('en-IN') }} unallocated
          </span>
        </div>

        <b-alert
          :show="!lifters.length"
          variant="warning"
          class="mt-1"
        >
          No approved lifters assigned to your company yet.
          <a
            href="#"
            @click.prevent="openLifters"
          >Add a lifter</a>.
        </b-alert>

        <div
          v-for="(row, i) in allocRows"
          :key="i"
          class="alloc-row"
        >
          <b-form-select
            v-model="row.lifter_id"
            :options="lifterOptions"
            size="sm"
            class="alloc-lifter"
          />
          <b-form-input
            v-model.number="row.qty_birds"
            type="number"
            min="1"
            placeholder="birds"
            size="sm"
            class="alloc-qty"
          />
          <b-form-input
            v-model.number="row.price_per_kg"
            type="number"
            step="0.01"
            placeholder="₹/kg"
            size="sm"
            class="alloc-price"
          />
          <b-form-select
            v-model="row.price_type"
            :options="priceTypeOptions"
            size="sm"
            class="alloc-ptype"
          />
          <b-button
            variant="flat-danger"
            size="sm"
            @click="allocRows.splice(i, 1)"
          >
            <feather-icon
              icon="XIcon"
              size="14"
            />
          </b-button>
        </div>

        <b-button
          variant="flat-primary"
          size="sm"
          class="mt-50"
          :disabled="!lifters.length"
          @click="addAllocRow"
        >
          <feather-icon
            icon="PlusIcon"
            size="14"
          /> Add lifter
        </b-button>

        <div class="alloc-foot">
          <b-button
            variant="outline-secondary"
            @click="allocModal = false"
          >
            Cancel
          </b-button>
          <b-button
            variant="primary"
            :disabled="saving || !allocRows.length || remaining < 0"
            @click="submitAllocation"
          >
            {{ saving ? 'Sending…' : 'Sell & notify lifters' }}
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- ===== Lifters modal (vet-style add/assign) ===== -->
    <b-modal
      v-model="liftersModal"
      title="Lifters"
      hide-footer
    >
      <div class="lm-add">
        <b-form-group label="Lifter phone">
          <b-input-group>
            <b-form-input
              v-model="lk.phone"
              placeholder="Registered phone"
            />
            <b-input-group-append>
              <b-button
                variant="outline-primary"
                @click="checkPhone"
              >
                Check
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>

        <div v-if="lk.checked">
          <div
            v-if="lk.exists"
            class="lm-found"
          >
            <div><strong>{{ lk.lifter.name }}</strong> · {{ lk.lifter.approval }}</div>
            <b-button
              v-if="!lk.already_assigned"
              size="sm"
              variant="primary"
              @click="submitLifter"
            >
              Assign to my company
            </b-button>
            <b-badge
              v-else
              variant="light-success"
            >
              Already assigned
            </b-badge>
          </div>
          <div
            v-else
            class="lm-new"
          >
            <b-form-group label="Name">
              <b-form-input v-model="lk.name" />
            </b-form-group>
            <b-form-group label="Email (optional)">
              <b-form-input v-model="lk.email" />
            </b-form-group>
            <b-button
              size="sm"
              variant="primary"
              :disabled="!lk.name"
              @click="submitLifter"
            >
              Submit for approval
            </b-button>
          </div>
        </div>
      </div>

      <hr>
      <h6>Assigned lifters</h6>
      <div
        v-if="!lifters.length"
        class="text-muted"
      >
        None yet.
      </div>
      <div
        v-for="l in lifters"
        :key="l.id"
        class="lm-row"
      >
        <span>{{ l.name }}</span><span class="text-muted">{{ l.phone }}</span>
      </div>
    </b-modal>

    <!-- ===== Settings modal ===== -->
    <b-modal
      v-model="settingsModal"
      title="Weight thresholds"
      hide-footer
    >
      <b-form-checkbox
        v-model="settings.auto_enabled"
        switch
        class="mb-1"
      >
        Automatic bird-sale alerts enabled
      </b-form-checkbox>
      <b-form-group label="Grade A ready (g)">
        <b-form-input
          v-model.number="settings.grade_a_weight"
          type="number"
        />
      </b-form-group>
      <b-form-group label="Grade A window closes / hold (g)">
        <b-form-input
          v-model.number="settings.grade_a_max_weight"
          type="number"
        />
      </b-form-group>
      <b-form-group label="Grade B ready (g)">
        <b-form-input
          v-model.number="settings.grade_b_weight"
          type="number"
        />
      </b-form-group>
      <div class="text-right">
        <b-button
          variant="primary"
          :disabled="savingSettings"
          @click="saveSettings"
        >
          {{ savingSettings ? 'Saving…' : 'Save' }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from '@/libs/axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
	name: 'BirdSaleManage',
	data() {
		return {
			mode: 'auto',
			viewMode: 'cards', // 'cards' | 'table'
			flockTableFields: [
				{ key: 'farm_name', label: 'Farm', sortable: true },
				{ key: 'shed_name', label: 'Shed', sortable: true },
				{ key: 'batch', label: 'Batch' },
				{ key: 'alive_birds', label: 'Birds', sortable: true },
				{ key: 'age_days', label: 'Age', sortable: true },
				{ key: 'avg_bird_weight', label: 'Avg wt', sortable: true },
				{ key: 'est_qty_kg', label: 'Est. kg', sortable: true },
				{ key: 'grade', label: 'Grade', sortable: true },
				{ key: 'actions', label: '' },
			],
			loadingFlocks: true,
			flocks: [],
			requests: [],
			lifters: [],

			allocModal: false,
			allocFlock: null,
			allocRows: [],
			saving: false,

			liftersModal: false,
			lk: {
				phone: '', checked: false, exists: false, already_assigned: false, lifter: null, name: '', email: '',
			},

			settingsModal: false,
			settings: {
				auto_enabled: true, grade_a_weight: 1700, grade_a_max_weight: 1800, grade_b_weight: 2200,
			},
			savingSettings: false,

			priceTypeOptions: [
				{ value: 'fixed', text: 'Fixed' },
				{ value: 'negotiable', text: 'Negotiable' },
			],
		}
	},
	computed: {
		lifterOptions() {
			return [{ value: null, text: 'Select lifter' }, ...this.lifters.map(l => ({ value: l.id, text: l.name }))]
		},
		// Flat list for the table view — sorted farm → shed so it reads top-to-bottom.
		tableFlocks() {
			return [...this.flocks].sort((a, b) => {
				const fa = (a.farm_name || '').localeCompare(b.farm_name || '')
				return fa !== 0 ? fa : (a.shed_name || '').localeCompare(b.shed_name || '')
			})
		},
		remaining() {
			const alloc = this.allocRows.reduce((s, r) => s + (Number(r.qty_birds) || 0), 0)
			return (this.allocFlock ? Number(this.allocFlock.alive_birds) : 0) - alloc
		},
		// Group ready flocks by farm so the grid reads farm-by-farm, not random.
		flocksByFarm() {
			const groups = {}
			this.flocks.forEach(f => {
				const key = f.farm_id ?? 'x'
				if (!groups[key]) {
					groups[key] = { farm_id: key, farm_name: f.farm_name || 'Farm', flocks: [], totalBirds: 0 }
				}
				groups[key].flocks.push(f)
				groups[key].totalBirds += Number(f.alive_birds) || 0
			})
			return Object.values(groups).sort((a, b) => a.farm_name.localeCompare(b.farm_name))
		},
	},
	created() {
		this.fetchFlocks()
		this.fetchRequests()
		this.fetchLifters()
	},
	methods: {
		toast(title, variant = 'success') {
			this.$toast({ component: ToastificationContent, props: { title, variant, icon: 'BellIcon' } })
		},
		setMode(m) {
			if (this.mode === m) return
			this.mode = m
			this.fetchFlocks()
		},
		async fetchFlocks() {
			this.loadingFlocks = true
			try {
				const { data } = await axios.get('web/bird-sale/ready-flocks', { params: { mode: this.mode } })
				this.flocks = data?.data ?? []
			} catch { this.flocks = [] } finally { this.loadingFlocks = false }
		},
		async fetchRequests() {
			try {
				const { data } = await axios.get('web/bird-sale/requests')
				this.requests = data?.data ?? []
			} catch { this.requests = [] }
		},
		async fetchLifters() {
			try {
				const { data } = await axios.get('web/lifters')
				this.lifters = data?.data ?? []
			} catch { this.lifters = [] }
		},
		reqPill(s) {
			return { approved: 'light-success', rejected: 'light-danger', withdrawn: 'light-secondary' }[s] || 'light-warning'
		},

		// ---- allocation ----
		openAllocate(f) {
			this.allocFlock = f
			this.allocRows = []
			this.addAllocRow()
			this.allocModal = true
		},
		addAllocRow() {
			this.allocRows.push({
				lifter_id: null,
				qty_birds: null,
				price_per_kg: null,
				price_type: 'fixed',
			})
		},
		async submitAllocation() {
			const rows = this.allocRows.filter(r => r.lifter_id && r.qty_birds > 0 && r.price_per_kg > 0)
			if (!rows.length) { this.toast('Add at least one complete allocation row', 'danger'); return }
			if (this.remaining < 0) { this.toast('You allocated more birds than available', 'danger'); return }
			this.saving = true
			try {
				await axios.post('web/bird-sale/create', {
					sell_mode: this.mode,
					flock_id: this.allocFlock.flock_id,
					bird_type: 'broiler',
					grade: this.allocFlock.grade,
					avg_bird_weight: this.allocFlock.avg_bird_weight || 1.7,
					allocations: rows,
				})
				this.toast('Sale created and lifters notified')
				this.allocModal = false
				this.fetchFlocks()
			} catch (e) {
				this.toast(e?.response?.data?.message ?? 'Could not create sale', 'danger')
			} finally { this.saving = false }
		},

		// ---- requests ----
		async approveRequest(r) {
			try { await axios.post(`web/bird-sale/requests/${r.id}/approve`); this.toast('Request approved'); this.fetchRequests() } catch { this.toast('Failed', 'danger') }
		},
		async rejectRequest(r) {
			try { await axios.post(`web/bird-sale/requests/${r.id}/reject`); this.toast('Request rejected'); this.fetchRequests() } catch { this.toast('Failed', 'danger') }
		},

		// ---- lifters (vet-style) ----
		openLifters() { this.liftersModal = true; this.fetchLifters() },
		async checkPhone() {
			if (!this.lk.phone) return
			try {
				const { data } = await axios.post('web/lifters/lookup', { phone: this.lk.phone })
				this.lk.checked = true
				this.lk.exists = !!data.exists
				this.lk.already_assigned = !!data.already_assigned
				this.lk.lifter = data.lifter || null
				this.lk.name = ''
			} catch { this.toast('Lookup failed', 'danger') }
		},
		async submitLifter() {
			try {
				const payload = this.lk.exists
					? { name: this.lk.lifter.name, phone: this.lk.phone }
					: { name: this.lk.name, phone: this.lk.phone, email: this.lk.email }
				const { data } = await axios.post('web/lifters', payload)
				this.toast(data.message || 'Saved')
				this.lk = {
					phone: '', checked: false, exists: false, already_assigned: false, lifter: null, name: '', email: '',
				}
				this.fetchLifters()
			} catch (e) { this.toast(e?.response?.data?.message ?? 'Failed', 'danger') }
		},

		// ---- settings ----
		async openSettings() {
			this.settingsModal = true
			try {
				const { data } = await axios.get('web/bird-sale/settings')
				if (data?.data) this.settings = data.data
			} catch { /* keep defaults */ }
		},
		async saveSettings() {
			this.savingSettings = true
			try {
				await axios.post('web/bird-sale/settings', this.settings)
				this.toast('Settings saved')
				this.settingsModal = false
			} catch (e) { this.toast(e?.response?.data?.message ?? 'Failed', 'danger') } finally { this.savingSettings = false }
		},
	},
}
</script>

<style scoped>
.bsm { max-width: 1100px; }
.bsm-head { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 1rem; }
.bsm-title { font-weight: 700; margin-bottom: 2px; }
.bsm-sub { color: #6e6b7b; font-size: 0.85rem; margin: 0; }
.bsm-head-actions { display: flex; gap: 0.5rem; }
.bsm-modes { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; margin-bottom: 1.25rem; }
.bsm-mode { display: flex; gap: 0.75rem; align-items: center; padding: 0.9rem 1rem; border: 1.5px solid rgba(34,41,47,0.12); border-radius: 0.6rem; cursor: pointer; background: #fff; }
.bsm-mode.active { border-color: #296DB4; background: rgba(41,109,180,0.05); color: #296DB4; }
.bsm-mode-t { font-weight: 700; font-size: 0.95rem; }
.bsm-mode-d { font-size: 0.75rem; color: #6e6b7b; }
.bsm-load, .bsm-empty { text-align: center; padding: 2.5rem 1rem; }
.bsm-viewtoggle { display: flex; align-items: center; justify-content: space-between; margin-bottom: 1rem; }
.bsm-viewtoggle-lbl { font-size: 0.8rem; color: #6e6b7b; font-weight: 600; }
.bsm-seg { display: inline-flex; border: 1px solid rgba(34,41,47,0.15); border-radius: 0.5rem; overflow: hidden; }
.bsm-seg-btn { display: inline-flex; align-items: center; gap: 0.35rem; padding: 0.4rem 0.85rem; font-size: 0.8rem; font-weight: 600; background: #fff; color: #6e6b7b; border: none; cursor: pointer; }
.bsm-seg-btn + .bsm-seg-btn { border-left: 1px solid rgba(34,41,47,0.15); }
.bsm-seg-btn.active { background: #296DB4; color: #fff; }
.bsm-table { font-size: 0.85rem; background: #fff; border-radius: 0.6rem; overflow: hidden; }
.bsm-farm-group { margin-bottom: 1.5rem; }
.bsm-farm-head { display: flex; align-items: center; padding: 0.6rem 0.9rem; margin-bottom: 0.75rem; background: rgba(41,109,180,0.06); border-left: 3px solid #296DB4; border-radius: 0.4rem; color: #296DB4; font-weight: 700; }
.bsm-farm-name { font-size: 0.98rem; }
.bsm-farm-count { margin-left: auto; font-size: 0.72rem; font-weight: 600; color: #6e6b7b; }
.bsm-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); gap: 0.9rem; }
.bsm-card { background: #fff; border: 1px solid rgba(34,41,47,0.09); border-radius: 0.7rem; padding: 1rem; }
.bsm-card-top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.75rem; }
.bsm-flock { font-weight: 700; }
.bsm-flock-sub { font-size: 0.78rem; color: #6e6b7b; }
.bsm-card-stats { display: flex; gap: 1rem; margin-bottom: 0.85rem; }
.bsm-card-stats .v { font-weight: 800; display: block; font-size: 1.05rem; }
.bsm-card-stats .l { font-size: 0.7rem; color: #6e6b7b; }
.bsm-req-head { display: flex; justify-content: space-between; align-items: center; margin: 1.75rem 0 0.5rem; }
.bsm-req-empty { font-size: 0.85rem; padding: 0.5rem 0; }
.bsm-req { display: flex; justify-content: space-between; align-items: center; padding: 0.75rem 1rem; background: #fff; border: 1px solid rgba(34,41,47,0.09); border-radius: 0.6rem; margin-bottom: 0.5rem; }
.bsm-req-lifter { font-weight: 600; }
.bsm-req-detail { font-size: 0.8rem; color: #6e6b7b; }
.bsm-req-actions { display: flex; gap: 0.35rem; align-items: center; }
.alloc-summary { display: flex; justify-content: space-between; font-size: 0.9rem; margin-bottom: 0.75rem; }
.alloc-row { display: flex; gap: 0.4rem; align-items: center; margin-bottom: 0.4rem; }
.alloc-lifter { flex: 2; } .alloc-qty { flex: 1; } .alloc-price { flex: 1; } .alloc-ptype { flex: 1.2; }
.alloc-foot { display: flex; justify-content: flex-end; gap: 0.5rem; margin-top: 1.25rem; }
.lm-found, .lm-new { padding: 0.75rem; background: rgba(34,41,47,0.03); border-radius: 0.5rem; margin-top: 0.5rem; }
.lm-found { display: flex; justify-content: space-between; align-items: center; }
.lm-row { display: flex; justify-content: space-between; padding: 0.4rem 0; border-bottom: 1px solid rgba(34,41,47,0.06); font-size: 0.88rem; }
</style>
